import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { DeleteRounded, ImageRounded } from '@mui/icons-material';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../lib/firebase';
import { errorText as apiErrorText } from '../../../lib/error-text';
import { safeImageUrl } from '../../../lib/featured-events';

const IMAGE_TYPES = /^image\/(png|jpeg|webp)$/;
const IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Imagen opcional de un elemento del landing (la franja de un evento, la foto
 * de un docente). Cada subida va a un archivo NUEVO (`{fileStem}-{marca}.{ext}`):
 * si se pisara el mismo nombre, el navegador del visitante podría seguir
 * mostrando la imagen anterior. El nombre es plano porque la regla de Storage
 * solo cubre `tenants/{id}/assets/{archivo}`.
 *
 * Los textos salen de `{i18nPrefix}.{label,hint,choose,replace,remove,
 * uploading,formats,unsupported,tooLarge,uploadError}`.
 */
export function LandingImageField({
  tenantId,
  fileStem,
  value,
  onChange,
  i18nPrefix,
  aspectRatio,
  previewWidth,
  fade = false,
}: {
  tenantId: string;
  fileStem: string;
  value: string;
  onChange: (url: string) => void;
  i18nPrefix: string;
  /** El recorte con que se verá en el landing, p. ej. '2 / 1' o '4 / 5'. */
  aspectRatio: string;
  previewWidth: number;
  /** El fundido inferior de la franja de los eventos. */
  fade?: boolean;
}) {
  const { t } = useTranslation('sis');
  const tx = (key: string) => t(`${i18nPrefix}.${key}`);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const preview = safeImageUrl(value);

  const upload = async (file: File) => {
    if (!IMAGE_TYPES.test(file.type)) {
      setError(tx('unsupported'));
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setError(tx('tooLarge'));
      return;
    }
    setError('');
    setUploading(true);
    const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const path = `tenants/${tenantId}/assets/${fileStem}-${Date.now()}.${ext}`;
    try {
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file, { contentType: file.type });
      onChange(await getDownloadURL(ref));
    } catch (e: unknown) {
      setError(apiErrorText(e, tx('uploadError')));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} mb={0.5}>
        {tx('label')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {tx('hint')}
      </Typography>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        {preview && (
          // Vista previa con el mismo recorte con que se verá en el landing.
          <Box
            sx={{
              position: 'relative',
              width: { xs: '100%', sm: previewWidth },
              maxWidth: '100%',
              aspectRatio,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: '#141717',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={preview}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {fade && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(180deg, rgba(13,15,15,0.25) 0%, rgba(13,15,15,0) 30%, rgba(20,23,23,0.55) 68%, #141717 100%)',
                }}
              />
            )}
          </Box>
        )}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={14} /> : <ImageRounded />}
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? tx('uploading') : preview ? tx('replace') : tx('choose')}
          </Button>
          {preview && !uploading && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteRounded />}
              onClick={() => onChange('')}
            >
              {tx('remove')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {tx('formats')}
          </Typography>
        </Stack>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
